import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { OrgContext } from "./PortalLayout";
import { listRequirements, getRequirement, submitRequirement, addRequirementComment } from "../../services/portalService";
import { errorToast, successToast } from "../Toasts/Toasts";

// Requirements: submit one (text, photo, PDF or Excel), see the list, open
// one with its lines as written, the conversation, attachments and where
// it stands. Prices appear only on the quotation.

export const statusClass = (s) => String(s || "").toLowerCase().replace(/\s+/g, "-");
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
export const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

export const NewRequirement = () => {
  const { org } = useContext(OrgContext);
  const navigate = useNavigate();
  const location = useLocation();
  // what they picked on the Plants page, already written out as a list
  const [text, setText] = useState((location.state && location.state.text) || "");
  const fromCatalogue = Boolean(location.state && location.state.text);
  const [siteId, setSiteId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [installation, setInstallation] = useState(false);
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!siteId && org?.sites?.length === 1) setSiteId(String(org.sites[0].id));
  }, [org, siteId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return errorToast("Type the requirement or attach a photo, PDF or Excel sheet");
    const form = new FormData();
    form.append("text", text);
    if (siteId) form.append("siteId", siteId);
    if (deliveryDate) form.append("deliveryDate", deliveryDate);
    form.append("installationWanted", installation ? "true" : "false");
    if (notes) form.append("notes", notes);
    for (const f of files) form.append("files", f);
    setBusy(true);
    try {
      const r = await submitRequirement(form);
      if (r?.success) {
        successToast(`${r.data.requirement.number} submitted`);
        navigate(`/portal/requirements/${r.data.requirement.number}`);
      } else errorToast(r?.message);
    } catch (err) {
      errorToast(err?.message || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h5 className="mb-3">New requirement</h5>
      <Form onSubmit={submit}>
        <div className="portal-card">
          <Form.Group className="mb-3">
            <Form.Label>What do you need?</Form.Label>
            <Form.Control as="textarea" rows={6} placeholder={"One item per line, with size and quantity, e.g.\nKadamb 4-5 ft - 40 nos\nNeem 6 ft - 25\nVermicompost - 200 kg"} value={text} onChange={(e) => setText(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Or attach a list — photo, PDF or Excel</Form.Label>
            <Form.Control type="file" multiple accept="image/*,.pdf,.xlsx,.xls,.csv" onChange={(e) => setFiles([...e.target.files])} />
            {files.length > 0 && <div className="small text-muted mt-1">{files.map((f) => f.name).join(", ")}</div>}
          </Form.Group>
        </div>
        <div className="portal-card">
          {org?.sites?.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Deliver to</Form.Label>
              <Form.Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">— choose a site —</option>
                {org.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Needed by</Form.Label>
            <Form.Control type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            {/* a list picked off the Plants page carries indicative prices only,
                so the date is what lets us answer on availability */}
            {fromCatalogue && <div className="small text-muted mt-1">We will confirm what is available for this date and send a quotation with GST and delivery to your site.</div>}
          </Form.Group>
          <Form.Check className="mb-3" type="switch" id="inst" label="Planting / installation needed" checked={installation} onChange={(e) => setInstallation(e.target.checked)} />
          <Form.Group>
            <Form.Label>Anything else</Form.Label>
            <Form.Control as="textarea" rows={2} placeholder="Gate, timing, contact on site…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Form.Group>
        </div>
        <Button type="submit" variant="success" className="w-100" disabled={busy}>{busy ? "Submitting…" : "Submit requirement"}</Button>
      </Form>
    </>
  );
};

export const RequirementList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { listRequirements().then(setRows).catch(() => setRows([])); }, []);
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Requirements</h5>
        <Link to="/portal/requirements/new" className="btn btn-success btn-sm">+ New</Link>
      </div>
      <div className="portal-card portal-list">
        {rows === null ? <div className="portal-empty">Loading…</div> : rows.length ? rows.map((r) => (
          <Link key={r.id} to={`/portal/requirements/${r.number}`} className="item d-block text-decoration-none text-reset">
            <div className="d-flex justify-content-between">
              <span className="fw-semibold">{r.number}</span>
              <span className={`status-pill ${statusClass(r.customerStatus)}`}>{r.customerStatus}</span>
            </div>
            <div className="small text-muted text-truncate">{r.originalText || "(attachment)"}</div>
            <div className="small text-muted">{fmtDate(r.createdAt)}{r.quoteNumber ? ` · ${r.quoteNumber} ${inr(r.quoteTotal)}` : ""}</div>
          </Link>
        )) : <div className="portal-empty">No requirements yet. <Link to="/portal/requirements/new">Submit one</Link>.</div>}
      </div>
    </>
  );
};

export const RequirementDetail = () => {
  const { number } = useParams();
  const [d, setD] = useState(null);
  const [comment, setComment] = useState("");
  const load = () => getRequirement(number).then(setD).catch((e) => errorToast(e?.message || "Not found"));
  useEffect(() => { load(); }, [number]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      const r = await addRequirementComment(number, comment);
      if (r?.success) { setComment(""); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };

  if (!d) return <div className="portal-empty">Loading…</div>;
  const r = d.requirement;
  const latest = d.versions?.find((v) => v.status !== "Superseded");
  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="mb-0">{r.number}</h5>
          <div className="small text-muted">{fmtDate(r.createdAt)}{r.siteName ? ` · ${r.siteName}` : ""}{r.deliveryDate ? ` · needed by ${fmtDate(r.deliveryDate)}` : ""}</div>
        </div>
        <span className={`status-pill ${statusClass(r.customerStatus)}`}>{r.customerStatus}</span>
      </div>

      {latest && (
        <Link to={`/portal/quotations/${latest.id}`} className="portal-action">
          {latest.status === "Issued" ? `Quotation ${latest.quoteNumber} is ready — ${inr(latest.totals.grand)}` : `Quotation ${latest.quoteNumber} · ${latest.status}`}
        </Link>
      )}
      {d.order && <Link to={`/portal/orders/${d.order.id}`} className="portal-action" style={{ background: "#2c7a4b" }}>Order {d.order.orderNumber} · {d.order.status}</Link>}

      <div className="portal-card">
        <h6>Items</h6>
        <div className="portal-list">
          {d.lines.map((l) => (
            <div className="item" key={l.id}>
              <div>{l.item}{l.isSeparateCharge ? " (charge)" : ""}</div>
              <div className="small text-muted">{[l.height, l.bagSize].filter(Boolean).join(", ")}{l.quantity ? ` · ${l.quantity} ${l.unit || ""}` : ""}</div>
            </div>
          ))}
        </div>
        {r.originalText && <details className="mt-2 small text-muted"><summary>As sent</summary><pre className="mb-0 mt-1" style={{ whiteSpace: "pre-wrap" }}>{r.originalText}</pre></details>}
        {r.customerNotes && <div className="small mt-2"><b>Notes:</b> {r.customerNotes}</div>}
        {r.installationWanted && <div className="small mt-1">Planting / installation requested</div>}
      </div>

      {d.documents?.length > 0 && (
        <div className="portal-card">
          <h6>Attachments</h6>
          {d.documents.map((doc) => <div key={doc.id}><a href={doc.url} target="_blank" rel="noreferrer">{doc.fileName}</a></div>)}
        </div>
      )}

      <div className="portal-card">
        <h6>Conversation</h6>
        <div className="portal-list">
          {d.comments.length ? d.comments.map((c) => (
            <div className="item" key={c.id}>
              <div className="small text-muted">{c.authorSide === "customer" ? c.authorName || "You" : "ManBhawan"} · {fmtDate(c.createdAt)}</div>
              <div>{c.text}</div>
            </div>
          )) : <div className="small text-muted">No messages yet.</div>}
        </div>
        <Form onSubmit={send} className="d-flex gap-2 mt-2">
          <Form.Control placeholder="Write a message to ManBhawan" value={comment} onChange={(e) => setComment(e.target.value)} />
          <Button type="submit" variant="outline-success">Send</Button>
        </Form>
      </div>
    </>
  );
};
